import React from "react"
import { Helmet } from "react-helmet-async"
import { Truck, Clock, MapPin, Shield, AlertCircle } from "lucide-react"

const ShippingPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Shipping Policy - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Read our detailed shipping policy. Orders shipped within 5 days through registered courier companies across India." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Shipping Policy</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding our shipping procedures and delivery commitments
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Shipping Overview */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Truck className="w-6 h-6 mr-3 text-primary-600" />
                Shipping Overview
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  The orders for users are shipped through registered domestic courier companies and/or speed post only. 
                  We ensure safe and timely delivery of your furniture and home decor items.
                </p>
              </div>
            </div>

            {/* Shipping Timeline */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Clock className="w-6 h-6 mr-3 text-primary-600" />
                Shipping Timeline
              </h2>
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-6">
                <div className="flex items-center text-blue-700 mb-2">
                  <Clock className="w-5 h-5 mr-2" />
                  <span className="font-semibold text-lg">Standard Shipping: 5 Days</span>
                </div>
                <p className="text-blue-600">
                  Orders are shipped within 5 days from the date of order confirmation and/or payment
                </p>
              </div>
              <div className="space-y-3 text-gray-700">
                <p>• Shipping timeline is calculated from the date of order and/or payment</p>
                <p>• Delivery date may be agreed upon at the time of order confirmation</p>
                <p>• Timeline subject to courier company and postal authority norms</p>
                <p>• Delivery confirmation will be sent to your registered email ID</p>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <MapPin className="w-6 h-6 mr-3 text-primary-600" />
                Delivery Address
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Delivery of all orders will be made to the address provided by the buyer at the time of purchase. 
                  Please ensure that you provide the complete and accurate delivery address including:
                </p>
                <ul className="text-gray-700 space-y-2 ml-6">
                  <li>Complete street address with house/flat number</li>
                  <li>Area/locality name</li>
                  <li>City, state, and PIN code</li>
                  <li>Contact number for delivery coordination</li>
                  <li>Landmark details (if applicable)</li>
                </ul>
              </div>
            </div>

            {/* Shipping Charges */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-primary-600" />
                Shipping Charges
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  If there are any shipping costs levied by the seller or Anmol Furniture Bazar, 
                  the same is not refundable. Shipping charges are calculated based on:
                </p>
                <ul className="text-gray-700 space-y-2 ml-6">
                  <li>Distance to delivery location</li>
                  <li>Size and weight of the items</li>
                  <li>Delivery zone classification</li>
                  <li>Special handling requirements</li>
                </ul>
              </div>
            </div>

            {/* Important Disclaimers */}
            <div className="bg-amber-50 p-6 rounded-xl border border-amber-200">
              <h2 className="text-xl font-semibold text-amber-900 mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                Important Notice
              </h2>
              <div className="space-y-3 text-amber-800">
                <p>
                  <strong>Delivery Delays:</strong> Platform Owner (Anmol Furniture Bazar) shall not be liable 
                  for any delay in delivery by the courier company or postal authority.
                </p>
                <p>
                  <strong>Force Majeure:</strong> We are not responsible for delays caused by circumstances 
                  beyond our control including natural disasters, strikes, or government regulations.
                </p>
                <p>
                  <strong>Contact Requirement:</strong> Please ensure your contact number is active as our 
                  delivery team will coordinate with you for the delivery schedule.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Questions About Shipping?</h2>
              <p className="text-gray-600 mb-6">
                For any shipping-related queries or concerns, please contact our customer service team
              </p>
              <div className="space-y-2">
                <p className="text-lg"><span className="font-semibold">Phone:</span> Available Monday - Friday (9:00 AM - 6:00 PM)</p>
                <p className="text-lg"><span className="font-semibold">Website:</span> https://www.anmolfurniturebazar.com/</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ShippingPolicy